"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { Star, Quote } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
const testimonials = [{
  id: 1,
  name: "Michael Chen",
  initials: "MC",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/avatar_michael.webp",
  text: "Our AC stopped cooling during hot weather. The pro arrived promptly, diagnosed a bad capacitor, and had our cooling running again quickly. Clear communication and upfront quote.",
  service: "AC Repair"
}, {
  id: 2,
  name: "Sarah Jenkins",
  initials: "SJ",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/avatar_sarah.webp",
  text: "Found a local pro through the network for a heat pump replacement. The crew was respectful of our home, laid down floor coverings, and the new heat pump works smoothly.",
  service: "System Installation"
}, {
  id: 3,
  name: "David Rodriguez",
  initials: "DR",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/avatar_david.png",
  text: "Scheduled seasonal maintenance for our HVAC system. The pro was thorough, explained each check step clearly, and provided honest recommendations.",
  service: "Seasonal Maintenance"
}];
export default function Testimonials() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="hvactestimonials" className="bg-background py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={cn("mx-auto max-w-2xl text-center transition-all duration-700 ease-out", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <span className="text-primary font-semibold tracking-wide uppercase text-sm">
            Customer Feedback
          </span>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-foreground sm:text-4xl font-default">
            Homeowner Experiences
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Read feedback from Atlanta homeowners who connected with local heating and cooling pros.
          </p>
        </div>

        <div ref={gridRef} className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {testimonials.map((testimonial, i) => <div key={testimonial.id} data-index={i} className={cn("transition-all duration-700 ease-out", isGridVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0")} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              <Card className="h-full flex flex-col shadow-md hover:shadow-lg transition-shadow duration-300 border-border/50">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-4">
                    <SafeHydrate><Avatar className="size-12 border border-border/50 shadow-sm">
                      <AvatarImage src={testimonial.avatar} data-wvc-srcset={`${testimonial.avatar}-wvc-srcset`} data-wvc-sizes={`${testimonial.avatar}-wvc-sizes`} alt={testimonial.name} />
                      <AvatarFallback className="bg-primary/10 text-primary font-medium">
                        {testimonial.initials}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div>
                      <CardTitle className="text-base font-default">
                        {testimonial.name}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Atlanta Homeowner
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-grow relative">
                  <Quote className="absolute top-0 left-4 size-10 text-muted/20 -z-10 transform -translate-y-2" />
                  <p className="text-muted-foreground text-sm leading-relaxed z-10 relative">
                    "{testimonial.text}"
                  </p>
                </CardContent>
                <CardFooter className="pt-4 border-t border-border/30">
                  <Badge variant="secondary" className="font-normal text-xs">
                    {testimonial.service}
                  </Badge>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}